import FAQs from '@components/FAQs';
import { ConfigLeftSection, ConfigMainGrid, ConfigRightSection, StyledGrid } from '@components/Grid';
import React, { FC, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { BRAND, CarJourneyType } from 'src/services';
import { SC } from 'src/styles/theme';
import OrderPanel from '../../v2/OrderPanel';
import Main from '@components/Layout/Main';
import { ISelectorPageProps } from 'src/pages/trim/configurable/[...model]';
import useTranslations from '@hooks/useTranslations';
import { setPageTitleDescriptionProperties } from '@utils/appConfig.utils';
import { buildParameterizedPathname } from '@utils/url.utils';
import { IS_B2B } from 'src/constants/main';
import routes from 'src/constants/routes';
import { useSelector } from 'react-redux';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import CustomError from '@components/CustomError';
import { NeedHelpSection } from '@components/NeedHelpSection';
import { useGTM } from '@hooks/useGTM';
import { ERROR_CODE } from 'src/pages/_error';
import { GtmData } from 'src/types/gtm';
import ImageSection from '../ImageSection';
import InterestedBox from '../InterestedBox';
import { Pages } from 'src/types/pages';
import TrimTitleSection from '../TrimTitleSection';
import TrimSelect from '../TrimSelect';
import FilterBox from '@components/SidebarFilterBox';
import useOnClickOutside from '@hooks/useOnClickOutside';
import Keywords from '@components/Keywords';
import { Redux } from 'src/redux/redux.interface';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import { useGlobalDuck } from '@hooks/useGlobalDuck';

export type IConfigurableTrimPropsSC = ISelectorPageProps & SC;

const ConfigurableTrimTemplate: FC<IConfigurableTrimPropsSC> = ({ nameplateBodyStyleSlug, className }) => {
    const { t } = useTranslations();
    const carDetailsDuc = useCarDetailsDuck();
    const { pushProductImpressions, pushTrimPageLoad, pushToDataLayer } = useGTM();
    const { carJourney } = useGlobalDuck();
    const allTrims = useSelector((state: Redux) => TrimSelectorDuck.getOwnState(state).configurable.trims);
    const filteredTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredTrims(state));
    const sampleFilteredTrim = filteredTrims[0];
    const fullBrand = t('title');

    const filterRef = useRef();

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const pageTitleDescriptionProperties = useMemo(
        () => setPageTitleDescriptionProperties(fullBrand, sampleFilteredTrim),
        [fullBrand, sampleFilteredTrim]
    );

    const pushTrimName = useCallback(
        (props: any) => {
            return pushToDataLayer({
                ...props,
                eventLabel: carDetailsDuc?.carDetails?.specPack?.title,
            } as GtmData);
        },
        [carDetailsDuc?.carDetails?.specPack?.title]
    );

    useEffect(() => {
        pushTrimPageLoad(allTrims, false);
        pushToDataLayer({
            eventCategory: 'SolRetail::VehicleFinition',
            eventAction: 'Select::VehicleModel::NonInteractive',
            eventLabel: 'Vehicle model selected',
        } as GtmData);
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        pushProductImpressions('trim', filteredTrims);
    }, [filteredTrims]);

    if (!allTrims.length) {
        return (
            <Main title={String(ERROR_CODE.NOT_FOUND)}>
                <CustomError
                    errorType={String(ERROR_CODE.NOT_FOUND)}
                    titleDescription={t('errorPage.notFound.description')}
                    returnLink={buildParameterizedPathname(routes.ROOT, carJourney)}
                    returnLinkLabel={t('errorPage.returnLink.label')}
                />
                <NeedHelpSection />
                <FAQs />
            </Main>
        );
    }

    const pageCanonicalLink = buildParameterizedPathname(routes.SELECTOR, carJourney, {
        nameplateBodyStyleSlug,
    });

    useOnClickOutside(filterRef, () => setIsFilterOpen(false), []);

    return (
        <div className={className}>
            <div ref={filterRef}>
                <FilterBox
                    onClickCheckbox={({ value, type }) =>
                        pushToDataLayer({
                            eventCategory: 'Step1',
                            eventAction: `Filter::${type}`,
                            eventLabel: value ? `Apply::${type}` : `Remove::${type}`,
                        } as GtmData)
                    }
                    isOpen={isFilterOpen}
                    closeFilter={() => setIsFilterOpen(false)}
                />
            </div>
            <Main
                title={
                    IS_B2B
                        ? t('seo.trimPage.b2b.title', pageTitleDescriptionProperties)
                        : t('seo.trimPage.title', pageTitleDescriptionProperties)
                }
                description={
                    IS_B2B
                        ? t('seo.trimPage.b2b.description', pageTitleDescriptionProperties)
                        : t('seo.trimPage.description', pageTitleDescriptionProperties)
                }
                canonicalLink={pageCanonicalLink}
                whiteBackground
                hasOpenFilter={isFilterOpen}
            >
                <OrderPanel
                    page={Pages.trim}
                    onSalesmanClick={(label) =>
                        pushToDataLayer({
                            event: 'uaevent',
                            eventCategory: 'SolRetail::VehicleFinition',
                            eventAction: 'Display::AdvisorForm',
                            eventLabel: label.replace(/[\W_]+/g, ' ').trim(),
                        } as GtmData)
                    }
                    onContinueBasketClick={() =>
                        pushTrimName({
                            event: 'uaevent',
                            eventCategory: 'SolRetail::VehicleFinition',
                            eventAction: 'Select::VehicleFinition',
                        })
                    }
                    onInfoIconClick={(label) =>
                        pushToDataLayer({
                            event: 'uaevent',
                            eventCategory: 'SolRetail::VehicleFinition',
                            eventAction: 'Display::FinanceDetails',
                            eventLabel: label.replace(/[\W_]+/g, ' ').trim(),
                        } as GtmData)
                    }
                    isWide
                    className="order-panel-top"
                />
                <StyledGrid brand={BRAND} noPadding isWide>
                    <ConfigMainGrid isWide>
                        <ConfigLeftSection>
                            <ImageSection isTrim />
                        </ConfigLeftSection>
                        <ConfigRightSection unsetMaxWidth>
                            <TrimTitleSection
                                onClickCTAFilters={(label) =>
                                    pushToDataLayer({
                                        event: 'uaevent',
                                        eventCategory: 'SolRetail::VehicleFinition',
                                        eventAction: `Display::Filters`,
                                        eventLabel: label.replace(/[\W_]+/g, ' ').trim(),
                                    } as GtmData)
                                }
                                openFilter={() => setIsFilterOpen(true)}
                            />
                            <TrimSelect
                                onTrimSelectClick={(label) =>
                                    pushToDataLayer({
                                        event: 'uaevent',
                                        eventCategory: 'Step1',
                                        eventAction: 'Choose::Finition',
                                        eventLabel: label.replace(/[\W_]+/g, ' '),
                                    } as GtmData)
                                }
                                onTrimFeaturesClick={(label) => {
                                    pushToDataLayer({
                                        event: 'uaevent',
                                        eventCategory: 'SolRetail::VehicleFinition',
                                        eventAction: 'Display::AllFeatures',
                                        eventLabel: label.replace(/[\W_]+/g, ' ').trim(),
                                    } as GtmData);
                                }}
                            />
                            <InterestedBox
                                onClickPhone={(label) =>
                                    pushToDataLayer({
                                        eventCategory: 'Content',
                                        eventAction: `Call::SOLRetail::${t('country')}`,
                                        eventLabel: label.replace(/[\W_]+/g, ' ').trim(),
                                    } as GtmData)
                                }
                                onClickCta={() =>
                                    pushTrimName({
                                        event: 'uaevent',
                                        eventCategory: 'SolRetail::VehicleFinition',
                                        eventAction: 'Select::VehicleFinition',
                                    })
                                }
                                onClickRequestMore={(label) =>
                                    pushToDataLayer({
                                        event: 'uaevent',
                                        eventCategory: 'SolRetail::VehicleFinition',
                                        eventAction: 'Display::AdvisorForm',
                                        eventLabel: label.replace(/[\W_]+/g, ' ').trim(),
                                    } as GtmData)
                                }
                                page={Pages.trim}
                                fullWidth
                                isLegalText
                            />
                            <Keywords />
                        </ConfigRightSection>
                    </ConfigMainGrid>
                </StyledGrid>
                <FAQs />
            </Main>
        </div>
    );
};

export default ConfigurableTrimTemplate;
